<template>
  <div class="moments-container">
    <!-- 发布朋友圈 -->
    <div class="publish-section">
      <el-card class="publish-card">
        <div class="publish-box">
          <head-image :name="mine.nickName" :size="45" :url="mine.headImageThumb"></head-image>
          <div class="publish-content">
            <el-input
              v-model="publishForm.content"
              type="textarea"
              :rows="3"
              placeholder="分享你的生活..."
              maxlength="500"
              show-word-limit
              resize="none"
            ></el-input>
            <div class="publish-actions">
              <div class="upload-section" v-if="publishForm.attachments.length > 0">
                <div
                  class="image-preview"
                  v-for="(attachment, index) in publishForm.attachments"
                  :key="`publish-attachment-${index}`"
                >
                  <img :src="attachment.thumbUrl || attachment.fileUrl" />
                  <i class="el-icon-close" @click="removeAttachment(index)"></i>
                </div>
              </div>
              <div class="action-buttons">
                <div class="upload-buttons">
                  <file-upload
                    :action="'/image/upload'"
                    :maxSize="10 * 1024 * 1024"
                    :fileTypes="['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/gif']"
                    :showLoading="true"
                    :isPermanent="true"
                    @success="onImageSuccess"
                    @fail="onImageFail"
                  >
                    <el-button size="small" icon="el-icon-picture" :disabled="publishForm.attachments.length >= 9">
                      图片 {{ publishForm.attachments.length }}/9
                    </el-button>
                  </file-upload>
                </div>
                <el-button type="primary" :loading="publishing" @click="showPublishDialog" size="small">发布</el-button>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 朋友圈列表 -->
    <div class="moments-list" v-loading="loading">
      <div v-for="(moment, index) in moments" :key="index" class="moment-item">
        <el-card class="moment-card">
          <div class="moment-header">
            <head-image :name="moment.nickName" :size="45" :url="moment.headImage"></head-image>
            <div class="moment-user-info">
              <div class="user-name">{{ moment.nickName }}</div>
              <div class="publish-time">{{ formatTime(moment.createdTime) }}</div>

            </div>
            <span
              v-if="moment.userId === userStore.userInfo.id"
              class="delete-moment-btn"
              @click="handleMomentAction({ action: 'delete', momentId: moment.id })"
            >删除</span>
            <!-- 审核状态标签 -->
            <el-tag 
              v-if="moment.userId === userStore.userInfo.id && moment.auditStatus !== undefined && moment.auditStatus !== 1" 
              :type="getAuditTagType(moment.auditStatus)" 
              size="mini" 
              class="audit-tag"
            >
              {{ getAuditStatusText(moment.auditStatus) }}
            </el-tag>
            <el-tag v-if="moment.isTop" type="danger" size="mini" class="top-tag">置顶</el-tag>
          </div>

          <div class="moment-content">
            <div class="content-text">{{ moment.content }}</div>
            <div class="content-images" v-if="moment.attachments && moment.attachments.length > 0">
              <template v-for="(attachment, index) in moment.attachments">
                <div
                  v-if="attachment.type === 0"
                  :key="`moment-${moment.id}-attachment-${attachment.id || index}`"
                  class="moment-image"
                  :class="`grid-${filteredImagesCount(moment)}`"
                  @click="previewImageByIndex(moment.attachments, index)"
                >
                  <img :src="attachment.thumbUrl || attachment.fileUrl" />
                </div>
              </template>
            </div>
          </div>

          <div class="moment-actions">
            <div class="action-item" @click="handleLike(moment)">
              <!-- el-icon-pointer 不存在于 Element 图标集中，改用 el-icon-mouse 作为鼠标指针图标 -->
              <i :class="moment.hasLiked ? 'el-icon-mouse active' : 'el-icon-mouse'"></i>
              <span>{{ moment.likeCount || '点赞' }}</span>
            </div>
            <div class="action-item" @click="focusComment(moment)">
              <i class="el-icon-chat-dot-round"></i>
              <span>{{ moment.commentCount || '评论' }}</span>
            </div>
          </div>

          <div class="moment-likes" v-if="moment.likes && moment.likes.length > 0">
            <!-- 使用鼠标指针图标代替缺失的 pointer -->
            <i class="el-icon-mouse like-icon"></i>
            <div class="likes-list">
              <span
                v-for="(like, index) in moment.likes"
                :key="index"
                class="like-user"
                @click="onLikeUserClick(like)"
                >{{ like.nickName }}</span
              >
            </div>
            <span class="likes-count"><i class="el-icon-mouse"></i>{{ moment.likeCount || 0 }}</span>
          </div>

          <div class="moment-comments" v-if="moment.comments && moment.comments.length > 0">
            <!-- 使用递归组件显示评论 -->
            <comment-item
              v-for="comment in getTopLevelComments(moment.comments)"
              :key="`comment-${comment.id}`"
              :comment="comment"
              :all-comments="moment.comments"
              :moment="moment"
              :level="0"
              @reply="handleReplyFromComponent"
              @delete="handleDeleteComment"
              @user-click="onCommentUserClick"
              @preview-image="previewCommentImage"
            />
          </div>

          <div class="comment-input" v-if="commentingMomentId === moment.id">
            <div class="comment-form">
              <el-input
                v-model="commentContent"
                :placeholder="replyTo ? `回复 ${replyTo.name} :` : '写下你的评论...'"
                size="small"
                type="textarea"
                :rows="3"
                resize="none"
                maxlength="500"
                show-word-limit
              ></el-input>

              <!-- 评论图片预览 -->
              <div class="comment-images-preview" v-if="commentImages.length > 0">
                <div
                  class="comment-image-item"
                  v-for="(img, index) in commentImages"
                  :key="`comment-img-${index}`"
                >
                  <img :src="img.thumbUrl || img.url" />
                  <i class="el-icon-close" @click="removeCommentImage(index)"></i>
                </div>
              </div>

              <div class="comment-actions">
                <div class="comment-upload">
                  <file-upload
                    :action="'/image/upload'"
                    :maxSize="5 * 1024 * 1024"
                    :fileTypes="['image/jpeg', 'image/png', 'image/jpg', 'image/webp']"
                    :showLoading="true"
                    :isPermanent="true"
                    @success="onCommentImageSuccess"
                    @fail="onCommentImageFail"
                  >
                    <el-button size="mini" icon="el-icon-picture" :disabled="commentImages.length >= 9">
                      图片 {{ commentImages.length }}/9
                    </el-button>
                  </file-upload>
                </div>
                <div class="comment-submit">
                  <el-button @click="cancelComment" size="small">取消</el-button>
                  <el-button type="primary" @click="submitComment(moment)" size="small">发送</el-button>
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </div>

      <!-- 加载更多 -->
      <div class="load-more" v-if="hasMore">
        <el-button @click="loadMore" :loading="loadingMore" type="text">加载更多</el-button>
      </div>
      <div class="no-more" v-else-if="moments.length > 0">没有更多了</div>
      <div class="empty-state" v-if="!loading && moments.length === 0">
        <i class="el-icon-picture-outline"></i>
        <p>还没有朋友圈动态</p>
      </div>
    </div>
    
    <!-- 发布确认对话框 -->
    <el-dialog
      title="选择可见范围"
      :visible.sync="publishDialogVisible"
      width="300px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div class="visibility-selection">
        <p>请选择这条朋友圈的可见范围：</p>
        <el-radio-group v-model="selectedVisibility" class="visibility-options">
          <el-radio :label="0">所有好友可见</el-radio>
          <el-radio :label="1">仅自己可见</el-radio>
          <el-radio :label="2">全局可见</el-radio>
        </el-radio-group>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelPublish" size="small">取消</el-button>
        <el-button type="primary" @click="confirmPublish" :loading="publishing" size="small">确认发布</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';
import FileUpload from '../common/FileUpload.vue';
import CommentItem from './CommentItem.vue';
import momentApi from '../../api/momentApi';

export default {
  name: 'Moments',
  components: {
    HeadImage,
    FileUpload,
    CommentItem,
  },
  data() {
    return {
      loading: false,
      loadingMore: false,
      publishing: false,
      moments: [],
      currentPage: 1,
      pageSize: 10,
      hasMore: true,
      hasLoaded: false, // 标记是否已加载过
      publishForm: {
        content: '',
        visibility: 0,
        attachments: [],
      },
      commentingMomentId: null,
      commentContent: '',
      replyTo: null, // 被回复的用户信息 { id, name }
      replyImage: null, // 要回复的图片URL
      commentImages: [], // 评论图片数组 [{url, thumbUrl, originUrl, width, height, fileName, fileSize}]
      publishDialogVisible: false, // 发布对话框显示状态
      selectedVisibility: 0, // 选中的可见范围 0:公开 1:仅自己可见
    };
  },
  computed: {
    mine() {
      return this.userStore?.userInfo || {};
    },
  },
  mounted() {
    // 添加点击外部区域监听
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    // 移除事件监听
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    init() {
      // 每次切换到朋友圈tab时都重新加载数据
      this.loadMoments(true);
      this.hasLoaded = true;
    },
    loadMoments(refresh = false) {
      if (refresh) {
        this.currentPage = 1;
        this.hasMore = true;
      }
      this.loading = true;
      momentApi
        .getMomentList(this.currentPage, this.pageSize)
        .then((data) => {
          console.log('朋友圈', data);
          if (refresh) {
            this.moments = data.records || [];
          } else {
            this.moments = [...this.moments, ...(data.records || [])];
          }
          this.hasMore = data.records && data.records.length >= this.pageSize;
        })
        .catch((err) => {
          console.error('加载朋友圈失败:', err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      this.loadingMore = true;
      this.currentPage++;
      momentApi
        .getMomentList(this.currentPage, this.pageSize)
        .then((data) => {
          const newMoments = data.records || [];
          this.moments = [...this.moments, ...newMoments];
          this.hasMore = newMoments.length >= this.pageSize;
        })
        .catch((err) => {
          console.error('加载更多失败:', err);
          this.currentPage--;
        })
        .finally(() => {
          this.loadingMore = false;
        });
    },
    async handleImageUpload(file) {
      try {
        // 获取图片尺寸
        const dimensions = await this.getImageDimensions(file);

        return {
          type: 0, // 0:图片
          fileUrl: typeof file.url === 'object' ? file.url.originUrl : file.url, // 原图URL
          thumbUrl:
            typeof file.thumbUrl === 'object'
              ? file.thumbUrl.thumbUrl
              : file.thumbUrl || (typeof file.url === 'object' ? file.url.thumbUrl : file.url), // 缩略图URL
          width: typeof file.url === 'object' ? file.url.width : dimensions.width, // 优先使用服务器返回的尺寸
          height: typeof file.url === 'object' ? file.url.height : dimensions.height,
          fileName: file.name,
          fileSize: file.size,
          sortOrder: this.publishForm.attachments.length,
        };
      } catch (error) {
        console.error('处理图片失败:', error);
        throw error;
      }
    },
    getImageDimensions(file) {
      return new Promise((resolve, reject) => {
        // 如果没有原始文件对象，返回默认尺寸
        if (!file || (!(file.raw instanceof File) && !(file instanceof File))) {
          resolve({ width: 800, height: 600 });
          return;
        }

        const actualFile = file.raw || file;
        const img = new Image();
        img.src = window.URL.createObjectURL(actualFile);
        img.onload = () => {
          window.URL.revokeObjectURL(img.src);
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
          // 加载失败返回默认尺寸
          resolve({ width: 800, height: 600 });
        };
      });
    },
    async onImageSuccess(data, file) {
      try {
        this.$message.success('图片上传成功');
        const attachment = await this.handleImageUpload({
          url: data, // 服务器返回的图片URL
          thumbUrl: data, // 缩略图URL，暂时使用原图
          name: file.name,
          size: file.size,
          rawFile: file.raw || file, // 确保有原始文件对象
        });
        this.publishForm.attachments.push(attachment);
      } catch (error) {
        console.error('添加附件失败:', error);
        this.$message.error('添加附件失败');
      }
    },
    onImageFail(error, _file) {
      console.error('图片上传失败:', error);
      this.$message.error('图片上传失败');
    },
    removeAttachment(index) {
      this.$confirm('确定删除这张图片吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.publishForm.attachments.splice(index, 1);
          // 重新排序
          this.publishForm.attachments.forEach((att, idx) => {
            att.sortOrder = idx;
          });
        })
        .catch(() => {});
    },
    // 显示发布对话框
    showPublishDialog() {
      if (!this.publishForm.content.trim() && this.publishForm.attachments.length === 0) {
        this.$message.warning('请输入内容或上传图片');
        return;
      }
      if (this.publishForm.content.length > 5000) {
        this.$message.warning('内容不能超过5000字符');
        return;
      }
      this.selectedVisibility = this.publishForm.visibility;
      this.publishDialogVisible = true;
    },
    
    // 确认发布
    confirmPublish() {
      this.publishing = true;
      const data = {
        content: this.publishForm.content,
        visibility: this.selectedVisibility,
        attachments: this.publishForm.attachments,
      };
      momentApi
        .publishMoment(data)
        .then(() => {
          this.$message.success('发布成功');
          this.publishDialogVisible = false;
          this.publishForm.content = '';
          this.publishForm.attachments = [];
          this.publishForm.visibility = 0;
          this.selectedVisibility = 0;
          this.loadMoments(true);
        })
        .catch((err) => {
          console.error('发布失败:', err);
          this.$message.error('发布失败');
        })
        .finally(() => {
          this.publishing = false;
        });
    },
    
    // 取消发布
    cancelPublish() {
      this.publishDialogVisible = false;
    },
    handleLike(moment) {
      if (moment.hasLiked) {
        momentApi
          .unlikeMoment(moment.id)
          .then(() => {
            moment.hasLiked = false;
            moment.likeCount = Math.max(0, (moment.likeCount || 1) - 1);
            this.$message.success('取消点赞');
          })
          .catch((err) => {
            console.error('取消点赞失败:', err);
          });
      } else {
        momentApi
          .commentMoment({
            momentId: moment.id,
            type: 2,
          })
          .then(() => {
            moment.hasLiked = true;
            moment.likeCount = (moment.likeCount || 0) + 1;
            this.$message.success('点赞成功');
          })
          .catch((err) => {
            console.error('点赞失败:', err);
          });
      }
    },
    focusComment(moment) {
      if (this.commentingMomentId === moment.id) {
        this.commentingMomentId = null;
      } else {
        this.commentingMomentId = moment.id;
        this.$nextTick(() => {
          const textarea = document.querySelector('.comment-input textarea');
          if (textarea) textarea.focus();
        });
        // 阻止事件冒泡，避免触发 handleClickOutside
        event.stopPropagation();
      }
      this.commentContent = '';
      this.replyTo = null;
      this.replyImage = null;
      this.commentImage = null;
    },
    // 获取一级评论（parentId 为 null 或等于自身 id 的评论）
    getTopLevelComments(comments) {
      return comments.filter((comment) => 
        comment.parentId == null || comment.parentId === comment.id
      );
    },

    // 从递归组件接收到的回复事件
    handleReplyFromComponent(comment, moment) {
      this.commentingMomentId = moment.id;
      // 构建回复信息
      this.replyTo = {
        id: comment.userId,
        name: comment.nickName,
        commentId: comment.id,
        parentId: comment.parentId || comment.id, // 使用评论的parentId，如果没有则使用自身id
      };
      this.commentContent = '';
      this.replyImage = null;
      this.commentImages = [];
      this.$nextTick(() => {
        const textarea = document.querySelector('.comment-input textarea');
        if (textarea) textarea.focus();
      });
      // 阻止事件冒泡，避免触发 handleClickOutside
      event.stopPropagation();
    },
    cancelComment() {
      this.commentingMomentId = null;
      this.commentContent = '';
      this.replyTo = null;
      this.replyImage = null;
      this.commentImages = [];
    },
    
    // 点击外部区域隐藏评论输入框
    handleClickOutside(event) {
      // 如果没有正在评论，直接返回
      if (!this.commentingMomentId) return;
      
      // 获取评论输入框元素
      const commentInput = document.querySelector('.comment-input');
      
      // 如果点击的不是评论输入框区域
      if (commentInput && !commentInput.contains(event.target)) {
        // 检查是否点击了回复相关按钮
        const replyElements = [
          '.reply-comment', 
          '.sub-reply-comment',
          '.comment-upload .el-button', // 上传按钮
          '.comment-submit .el-button'  // 提交/取消按钮
        ];
        
        const isClickOnInteractiveElement = replyElements.some(selector => {
          const elements = document.querySelectorAll(selector);
          return Array.from(elements).some(el => el.contains(event.target));
        });
        
        // 如果不是点击交互元素且输入框无内容，则隐藏
        if (!isClickOnInteractiveElement && !this.commentContent.trim() && !this.commentImage) {
          this.cancelComment();
        }
      }
    },
    submitComment(moment) {
      if (!this.commentContent.trim() && this.commentImages.length === 0) {
        this.$message.warning('请输入评论内容或上传图片');
        return;
      }
          
      const data = {
        momentId: moment.id,
        content: this.commentContent,
        type: 1, // 1:评论
      };
          
      // 如果有评论图片，添加attachments
      if (this.commentImages.length > 0) {
        data.attachments = this.commentImages.map((img, index) => ({
          type: 0, // 0:图片
          fileUrl: img.originUrl || img.url,
          thumbUrl: img.thumbUrl || img.originUrl || img.url,
          fileName: img.fileName || `image_${index + 1}.jpg`,
          fileSize: img.fileSize || 0,
          width: img.width || 0,
          height: img.height || 0,
          sortOrder: index
        }));
      }
          
      // 如果有被回复的用户，添加toUserId和parentId
      if (this.replyTo && this.replyTo.id) {
        data.toUserId = this.replyTo.id;
        data.parentId = this.replyTo.parentId;
      }
          
      momentApi
        .commentMoment(data)
        .then(() => {
          this.$message.success('评论成功，审核通过后对方才可见');
          this.cancelComment();
          this.loadMoments(true);
        })
        .catch((err) => {
          console.error('评论失败:', err);
          this.$message.error('评论失败');
        });
    },
    onCommentImageSuccess(data, file) {
      try {
        this.$message.success('图片上传成功');
        // 保存完整的上传数据到数组中
        this.commentImages.push({
          url: data.thumbUrl || data.originUrl || data,
          originUrl: data.originUrl,
          thumbUrl: data.thumbUrl,
          fileName: file.name,
          fileSize: file.size,
          width: data.width || 0,
          height: data.height || 0
        });
      } catch (error) {
        console.error('添加评论图片失败:', error);
        this.$message.error('添加图片失败');
      }
    },
    onCommentImageFail(error, _file) {
      console.error('评论图片上传失败:', error);
      this.$message.error('图片上传失败');
    },
    removeCommentImage(index) {
      this.commentImages.splice(index, 1);
    },
    previewCommentImage(imageUrl) {
      this.$eventBus.$emit('openFullImage', imageUrl);
    },
    
    // 点击评论用户头像/昵称
    onCommentUserClick(user) {
      console.log('点击评论用户:', user);
      // 这里可以添加跳转到用户详情的逻辑
      // this.$router.push(`/user/${user.userId}`);
    },
    handleDeleteComment(commentId) {
      this.$confirm('确定删除这条评论吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          momentApi
            .deleteComment(commentId)
            .then(() => {
              this.$message.success('删除成功');
              this.loadMoments(true);
            })
            .catch((err) => {
              console.error('删除评论失败:', err);
            });
        })
        .catch(() => {});
    },
    handleMomentAction(command) {
      if (command.action === 'delete') {
        this.$confirm('确定删除这条朋友圈吗?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        })
          .then(() => {
            momentApi
              .deleteMoment({ momentId: command.momentId })
              .then(() => {
                this.$message.success('删除成功');
                this.loadMoments(true);
              })
              .catch((err) => {
                console.error('删除失败:', err);
              });
          })
          .catch(() => {});
      } else if (command.action === 'top') {
        momentApi
          .setMomentTop({
            momentId: command.momentId,
            isTop: command.isTop,
          })
          .then(() => {
            this.$message.success(command.isTop ? '置顶成功' : '取消置顶成功');
            this.loadMoments(true);
          })
          .catch((err) => {
            console.error('置顶操作失败:', err);
          });
      }
    },
    previewImage(attachments, index) {
      const imageAttachments = attachments.filter((a) => a.type === 0);
      if (imageAttachments[index]) {
        this.$eventBus.$emit('openFullImage', imageAttachments[index].fileUrl);
      }
    },
    filteredImagesCount(moment) {
      return (moment.attachments || []).filter((a) => a.type === 0).length;
    },
    previewImageByIndex(attachments, index) {
      const attachment = attachments[index];
      if (attachment && attachment.type === 0) {
        this.$eventBus.$emit('openFullImage', attachment.fileUrl);
      } else {
        // 如果不是图片或获取失败，尝试过滤后重新获取
        const imageAttachments = attachments.filter((a) => a.type === 0);
        if (imageAttachments[index]) {
          this.$eventBus.$emit('openFullImage', imageAttachments[index].fileUrl);
        }
      }
    },
    onLikeUserClick(like) {
      // 点击点赞用户，可以跳转到用户详情或打开用户信息
      console.log('点击点赞用户:', like);
      // 这里可以添加跳转逻辑，例如：
      // this.$router.push(`/user/${like.userId}`);
      // 或者打开用户信息弹窗
    },
    formatTime(time) {
      if (!time) return '';
      const now = new Date();
      const timestamp = new Date(time);
      const diff = now - timestamp;

      const minute = 60 * 1000;
      const hour = 60 * minute;
      const day = 24 * hour;

      if (diff < minute) {
        return '刚刚';
      } else if (diff < hour) {
        return Math.floor(diff / minute) + '分钟前';
      } else if (diff < day) {
        return Math.floor(diff / hour) + '小时前';
      } else if (diff < day * 2) {
        return '昨天';
      } else {
        const date = new Date(time);
        return `${date.getMonth() + 1}月${date.getDate()}日`;
      }
    },
    // 获取审核状态标签类型
    getAuditTagType(auditStatus) {
      switch (auditStatus) {
        case 0: // 待审核
          return 'warning';
        case 2: // 审核拒绝
          return 'danger';
        default:
          return 'info';
      }
    },
    // 获取审核状态文本
    getAuditStatusText(auditStatus) {
      switch (auditStatus) {
        case 0:
          return '待审核';
        case 1:
          return '审核通过';
        case 2:
          return '审核拒绝';
        default:
          return '未知状态';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.moments-container {
  height: 100%;
  overflow-y: auto;
  background: transparent;
  padding: 20px;

  .publish-section {
    margin-bottom: 20px;

    .publish-card {
      border-radius: var(--radius-md);

      .publish-box {
        display: flex;
        gap: 15px;

        .publish-content {
          flex: 1;

          .publish-actions {
            margin-top: 15px;

            .upload-section {
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
              margin-bottom: 15px;

              .image-preview {
                position: relative;
                width: 80px;
                height: 80px;
                border-radius: var(--radius-xs);
                overflow: hidden;

                img {
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                }

                .el-icon-close {
                  position: absolute;
                  top: 2px;
                  right: 2px;
                  cursor: pointer;
                  color: #fff;
                  background: rgba(0, 0, 0, 0.5);
                  border-radius: 50%;
                  padding: 3px;
                  font-size: 14px;
                }
              }
            }

            .action-buttons {
              display: flex;
              justify-content: space-between;
              align-items: center;

              .upload-buttons {
                display: flex;
                gap: 10px;

                ::v-deep .el-button {
                  display: flex;
                  align-items: center;
                }
              }
            }
          }
        }
      }
    }
  }

  .moments-list {
    .moment-item {
      margin-bottom: 20px;

      .moment-card {
        border-radius: var(--radius-md);

        .moment-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 15px;
          position: relative;

          .moment-user-info {
            flex: 1;
            margin-left: 8px;
            text-align: left;

            .user-name {
              font-size: 15px;
              font-weight: 600;
              color: var(--im-text-color);
            }

            .publish-time {
              font-size: 12px;
              color: var(--im-text-color-secondary);
              margin-top: 2px;
            }
            
            .debug-info {
              font-size: 10px;
              color: #FF3B30;
              margin-top: 2px;
              background: rgba(255, 59, 48, 0.10);
              padding: 2px 4px;
              border-radius: 2px;
            }
          }

          .delete-moment-btn {
            cursor: pointer;
            color: #FF3B30;
            font-size: 13px;
            padding: 4px 8px;
            &:hover {
              opacity: 0.75;
            }
          }

          .action-menu {
            cursor: pointer;
            color: var(--im-text-color-secondary);
            font-size: 16px;
            padding: 8px;

            &:hover {
              color: var(--aurora-blue);
            }
          }

          .top-tag {
            position: absolute;
            right: 0;
            top: 0;
          }
          
          .audit-tag {
            margin-right: 10px;
          }
        }

        .moment-content {
          margin-bottom: 15px;
          text-align: left;

          .content-text {
            font-size: 14px;
            color: var(--im-text-color);
            line-height: 1.6;
            margin-bottom: 12px;
            white-space: pre-wrap;
            word-break: break-all;
            text-align: left;
          }

          .content-images {
            display: grid;
            gap: 6px;
            max-width: 400px;

            .moment-image {
              width: 100px;
              height: 100px;
              border-radius: var(--radius-md);
              overflow: hidden;
              cursor: pointer;

              img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                transition: transform 0.3s;

                &:hover {
                  transform: scale(1.02);
                }
              }

              &.grid-1 {
                grid-template-columns: 1fr;
              }

              &.grid-2 {
                grid-template-columns: repeat(2, 1fr);
              }

              &.grid-3,
              &.grid-5,
              &.grid-6,
              &.grid-7,
              &.grid-8,
              &.grid-9 {
                grid-template-columns: repeat(3, 1fr);
              }

              &.grid-3 .moment-image {
                &:nth-child(3) {
                  grid-column: 1 / 3;
                  width: 200px;
                }
              }
            }
          }
        }

        .moment-actions {
          display: flex;
          gap: 30px;
          padding: 10px 0;
          border-top: 0.5px solid var(--hairline-soft);
          border-bottom: 0.5px solid var(--hairline-soft);
          margin-bottom: 10px;

          .action-item {
            display: flex;
            align-items: center;
            gap: 6px;
            cursor: pointer;
            font-size: 14px;
            color: var(--im-text-color-secondary);
            transition: all 0.3s;

            i {
              font-size: 18px;
              transition: all 0.3s;

              &.active {
                color: var(--aurora-blue);
              }
            }

            &:hover {
              color: var(--aurora-blue);
            }
          }
        }

        .moment-likes {
          background: rgba(255, 252, 243, 0.62); backdrop-filter: blur(20px) saturate(140%); -webkit-backdrop-filter: blur(20px) saturate(140%); border: 0.5px solid var(--hairline-soft);
          padding: 10px 12px;
          border-radius: var(--radius-md);
          margin-bottom: 10px;
          font-size: 13px;
          display: flex;
          align-items: flex-start;
          gap: 6px;

          .like-icon {
            color: #FF3B30;
            font-size: 14px;
            margin-top: 2px;
          }

          .likes-list {
            flex: 1;
            color: var(--im-text-color);
            line-height: 1.6;
            text-align: left;

            .like-user {
              display: inline-block;
              color: var(--aurora-blue);
              cursor: pointer;
              font-weight: 500;
              margin: 0 4px;
              white-space: nowrap;

              &:hover {
                text-decoration: underline;
              }
            }
          }

          .likes-count {
            display: flex;
            align-items: center;
            gap: 4px;
            color: #FF3B30;
            font-weight: 500;

            i {
              font-size: 12px;
            }
          }
        }

        .moment-comments {
          background: rgba(255, 252, 243, 0.62); backdrop-filter: blur(20px) saturate(140%); -webkit-backdrop-filter: blur(20px) saturate(140%); border: 0.5px solid var(--hairline-soft);
          padding: 12px;
          border-radius: var(--radius-md);
          text-align: left;

          .comment-item {
            display: flex;
            gap: 10px;
            margin-bottom: 15px;
            font-size: 13px;
            align-items: flex-start;

            &:last-child {
              margin-bottom: 0;
            }

            ::v-deep {
              img {
                border-radius: 50%;
                flex-shrink: 0;
              }
            }

            .comment-content {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 6px;

              .comment-text-line {
                line-height: 1.6;

                .comment-user {
                  color: var(--aurora-blue);
                  font-weight: 500;
                  cursor: pointer;
                }

                .reply-to,
                .reply-user {
                  color: var(--im-text-color-secondary);
                }

                .comment-text {
                  color: var(--im-text-color);
                }
              }

              /* 评论图片 */
              .comment-images {
                display: flex;
                gap: 6px;
                flex-wrap: wrap;

                .comment-image {
                  width: 80px;
                  height: 80px;
                  border-radius: var(--radius-xs);
                  overflow: hidden;
                  cursor: pointer;

                  img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.3s;

                    &:hover {
                      transform: scale(1.05);
                    }
                  }
                }
              }

              .comment-actions {
                display: flex;
                gap: 12px;
                font-size: 12px;

                .comment-time {
                  color: var(--im-text-color-lighter);
                }

                .reply-comment {
                  color: var(--im-text-color-secondary);
                  cursor: pointer;

                  &:hover {
                    color: var(--aurora-blue);
                  }
                }

                .delete-comment {
                  color: var(--im-text-color-secondary);
                  cursor: pointer;

                  &:hover {
                    color: var(--im-color-danger);
                  }
                }
              }

              /* 子评论区域 */
              .sub-comments {
                margin-top: 10px;
                padding-left: 15px;
                border-left: 2px solid; border-image: linear-gradient(180deg, var(--aurora-blue), var(--aurora-purple)) 1;

                .sub-comment-item {
                  background: rgba(255, 255, 255, 0.62); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border: 0.5px solid var(--hairline-soft);
                  padding: 8px 12px;
                  border-radius: var(--radius-sm);
                  margin-bottom: 8px;
                  font-size: 12px;
                  line-height: 1.5;

                  &:last-child {
                    margin-bottom: 0;
                  }

                  .sub-comment-user {
                    color: var(--aurora-blue);
                    font-weight: 500;
                    cursor: pointer;
                  }

                  .sub-reply-to {
                    color: var(--im-text-color-secondary);
                    margin: 0 4px;
                  }

                  .sub-reply-user {
                    color: var(--aurora-blue);
                    font-weight: 500;
                    cursor: pointer;
                  }

                  .sub-comment-text {
                    color: var(--im-text-color);
                  }

                  /* 子评论图片 */
                  .sub-comment-images {
                    display: flex;
                    gap: 4px;
                    flex-wrap: wrap;
                    margin: 6px 0;

                    .sub-comment-image {
                      width: 60px;
                      height: 60px;
                      border-radius: var(--radius-xs);
                      overflow: hidden;
                      cursor: pointer;

                      img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        transition: transform 0.3s;

                        &:hover {
                          transform: scale(1.05);
                        }
                      }
                    }
                  }

                  .sub-comment-actions {
                    display: flex;
                    gap: 10px;
                    margin-top: 4px;
                    font-size: 11px;

                    .sub-comment-time {
                      color: var(--im-text-color-lighter);
                    }

                    .sub-reply-comment {
                      color: var(--im-text-color-secondary);
                      cursor: pointer;

                      &:hover {
                        color: var(--aurora-blue);
                      }
                    }

                    .sub-delete-comment {
                      color: var(--im-text-color-secondary);
                      cursor: pointer;

                      &:hover {
                        color: var(--im-color-danger);
                      }
                    }
                  }
                }
              }
            }
          }
        }

        .comment-input {
          margin-top: 15px;

          .comment-form {
            .comment-images-preview {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              margin: 10px 0;

              .comment-image-item {
                position: relative;
                width: 60px;
                height: 60px;
                border-radius: var(--radius-xs);
                overflow: hidden;

                img {
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                }

                .el-icon-close {
                  position: absolute;
                  top: 2px;
                  right: 2px;
                  cursor: pointer;
                  color: #fff;
                  background: rgba(0, 0, 0, 0.5);
                  border-radius: 50%;
                  padding: 2px;
                  font-size: 12px;
                }
              }
            }

            .comment-actions {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-top: 10px;

              .comment-upload {
                ::v-deep .el-button {
                  display: flex;
                  align-items: center;
                }
              }

              .comment-submit {
                display: flex;
                gap: 8px;
              }
            }
          }
        }
      }
    }

    .load-more {
      text-align: center;
      padding: 20px 0;

      .el-button {
        color: var(--im-text-color-secondary);
        font-size: 14px;

        &:hover {
          color: var(--aurora-blue);
        }
      }
    }

    .no-more {
      text-align: center;
      padding: 20px 0;
      color: var(--im-text-color-lighter);
      font-size: 13px;
    }

    .empty-state {
      text-align: center;
      padding: 60px 0;
      color: var(--im-text-color-lighter);

      i {
        font-size: 64px;
        margin-bottom: 16px;
      }

      p {
        font-size: 14px;
      }
    }
  }
}

::v-deep .el-textarea__inner {
  border: 0.5px solid var(--hairline-mid);
  background: rgba(255, 255, 255, 0.55); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
  padding: 8px 12px;
  border-radius: var(--radius-xs);

  &:focus {
    border-color: var(--aurora-blue);
    box-shadow: 0 0 0 2px rgba(91, 141, 238, 0.18);
  }
}

::v-deep .el-upload {
  .el-upload__input {
    display: none;
  }
}

/* 发布对话框样式 */
.visibility-selection {
  text-align: center;
  padding: 20px 0;
  
  p {
    margin-bottom: 20px;
    color: var(--im-text-color);
    font-size: 14px;
  }
  
  .visibility-options {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    
    ::v-deep .el-radio {
      margin-right: 0;
      
      .el-radio__label {
        font-size: 14px;
        color: var(--im-text-color);
      }
    }
  }
}

.dialog-footer {
  text-align: right;
  
  .el-button {
    margin-left: 10px;
  }
}

/* === Aurora glass overlay for moments cards === */
::v-deep .el-card {
  background: rgba(255, 252, 243, 0.62) !important;
  backdrop-filter: blur(24px) saturate(140%);
  -webkit-backdrop-filter: blur(24px) saturate(140%);
  border: 0.5px solid var(--glass-border) !important;
  border-radius: var(--radius-lg) !important;
  box-shadow:
    inset 0 1px 0 0 var(--glass-border-inner),
    0 1px 2px rgba(31, 31, 35, 0.04),
    0 8px 24px rgba(31, 31, 35, 0.06) !important;
  transition: transform 0.2s ease, box-shadow 0.24s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow:
      inset 0 1px 0 0 var(--glass-border-inner),
      0 12px 28px rgba(91, 141, 238, 0.12) !important;
  }

  .el-card__body { padding: 18px 20px; }
}

/* moment header user name aurora highlight */
.moments-container ::v-deep .user-name { letter-spacing: -0.3px; }

/* upload close icon polish */
.moments-container .el-icon-close {
  background: rgba(31, 31, 35, 0.55) !important;
  backdrop-filter: blur(8px);
}
</style>
